import { ApplicationPurposeCodeEnum } from '../../modules/application/shared/constants/application-purpose-codes';
import { RouteKey } from '../models/route-key';

class ApplicationApiRoutes {
    static readonly rootUrl: string = 'api/applications';

    static readonly customerApplicationsPath: string = 'customer';

    static getRootUrlWithApplicationId(applicationId: string) {
        return `${this.rootUrl}/${applicationId}`;
    }

    private static readonly directEntryBaseUrl: string = 'direct-entry';
    private static readonly coordinationBaseUrl: string = 'coordination';

    static getFullRouteWithApplicationId(
        route: RouteKey<typeof ApplicationApiRoutes>,
        applicationId: string,
    ) {
        return `${this.getRootUrlWithApplicationId(applicationId)}/${ApplicationApiRoutes[route]}`;
    }

    static readonly allStepsValid = `${this.directEntryBaseUrl}/all-steps-valid`;
    static readonly validateSteps = `${this.directEntryBaseUrl}/validate-steps`;
    static readonly generalInfo = `${this.directEntryBaseUrl}/general-info`;
    static readonly feesWaiversExemptions = `${this.directEntryBaseUrl}/fees-waivers-exemptions`;
    static readonly applicantInfo = `${this.directEntryBaseUrl}/applicant-info`;
    static readonly scheduleHAdditionalInformation = `${this.directEntryBaseUrl}/schedule-h-additional-information`;
    static readonly scheduleDAndH = `${this.directEntryBaseUrl}/schedules-d-and-h`;
    static readonly scheduleK = `${this.directEntryBaseUrl}/schedule-k`;
    static readonly regulatoryInfo = `${this.directEntryBaseUrl}/regulatory-info`;
    static readonly certificationInfo = `${this.directEntryBaseUrl}/certification-info`;
    static readonly attachmentsAndWaivers = `${this.directEntryBaseUrl}/attachments-and-waivers`;
    static readonly submitToEwa = `submit-to-ewa`;
    static readonly submitToFcc = `submit-to-fcc`;
    static readonly submitToPsca = `submit-to-psca`;
    static readonly submitToFiveDayHold = `submit-to-five-day-hold`;
    static readonly state = 'state';
    static readonly advisorViewStatus = 'advisor-view-status';
    static readonly directEntrySteps = 'direct-entry-steps';
    static readonly safeHarbor = `check-safe-harbor`;
    static readonly applicationExport = 'export';

    static readonly coordinationParameters = `${this.coordinationBaseUrl}/parameters`;
    static readonly coordinationResults = `${this.coordinationBaseUrl}/results`;
    static readonly coordinationResultStudies = `${this.coordinationBaseUrl}/studies`;
    static readonly coordinationStatus = `${this.coordinationBaseUrl}/status`;
    static readonly coordinationSubmitToEwa = `${this.coordinationBaseUrl}/submit-to-ewa`;
    static readonly submitForCoordination = `${this.coordinationBaseUrl}/coordinate-application`;

    static getCoordinationResultStudyDetailsRoute(
        applicationId: string,
        studyId: string,
    ) {
        return `${this.getFullRouteWithApplicationId('coordinationResultStudies', applicationId)}/${studyId}`;
    }
}

class UnsupportedApplicationApiRoutes {
    static readonly rootUrl: string = 'api/unsupported-applications';
    static readonly submitToEwa = 'submit-to-ewa';

    static getRootUrlWithApplicationId(applicationId: string | number) {
        return `${this.rootUrl}/${applicationId}`;
    }

    static getFullRouteWithApplicationId(
        route: RouteKey<typeof UnsupportedApplicationApiRoutes>,
        applicationId: string,
    ) {
        return `${this.getRootUrlWithApplicationId(applicationId)}/${UnsupportedApplicationApiRoutes[route]}`;
    }
}

class ApplicationTemplateApiRoutes {
    static readonly rootUrl: string = 'api/application-templates';

    static readonly exists: string = `${this.rootUrl}/exists`;
    static readonly createFromApplication: string = `${this.rootUrl}/from-application`;
    static readonly applicationPurposeCodesForTemplateCreation: string = `${this.rootUrl}/application-purpose-codes-for-template-creation`;

    static getRootWithTemplateId(templateId: number | string) {
        return `${this.rootUrl}/${templateId}`;
    }

    static getAllTemplatesRoute(
        applicationPurpose: ApplicationPurposeCodeEnum | null,
    ) {
        return applicationPurpose
            ? `${this.rootUrl}?applicationPurpose=${applicationPurpose}`
            : this.rootUrl;
    }
}

class FrnFilingApiRoutes {
    static readonly rootUrl: string = 'api/frn-filing';
    static readonly applicationRootUrl: string = 'api/applications';
    static readonly type: string = 'type';
    static readonly detail: string = 'detail';
    static readonly validatePreviousSteps: string = 'validate-previous-steps';
    static readonly validateAllSteps: string = 'validate-all-steps';
    static readonly submitToEwa: string = 'submit-to-ewa';
    static readonly syncToDirectEntry: string = 'sync-to-direct-entry';

    static getRootUrlWithApplicationId(applicationId: string | number): string {
        return `${this.applicationRootUrl}/${applicationId}/frn-filing`;
    }

    static getFullRouteWithApplicationId(
        route: RouteKey<typeof FrnFilingApiRoutes>,
        applicationId: string | number,
    ): string {
        return `${this.getRootUrlWithApplicationId(applicationId)}/${FrnFilingApiRoutes[route]}`;
    }
}

class ServiceFulfillmentApiRoutes {
    static getRootUrl(): string {
        return 'api/service-fulfillment';
    }

    static getSubmitToEwaUrl(applicationId: number): string {
        return `${this.getRootUrl()}/${applicationId}/submit-to-ewa`;
    }

    static getUpdateApplicationWorkflowStateUrl(): string {
        return `${this.getRootUrl()}/workflow-state`;
    }

    static getUpdateApplicationAdvisorUrl(): string {
        return `${this.getRootUrl()}/advisor`;
    }
}

class OrderApiRoutes {
    static getRootUrl(): string {
        return 'api/orders';
    }

    static getByIdUrl(orderId: string): string {
        return `${this.getRootUrl()}/${orderId}`;
    }

    static getUpdateStatusUrl(orderId: string): string {
        return `${this.getByIdUrl(orderId)}/status`;
    }

    static getUserOrdersUrl(userId: number): string {
        return `api/users/${userId}/orders`;
    }

    static getSetPreferredPaymentMethodUrl(orderId: string): string {
        return `${this.getByIdUrl(orderId)}/set-preferred-payment-method`;
    }

    static getBillingDocumentPdfUrl(orderId: string): string {
        return `${this.getRootUrl()}/${orderId}/export`;
    }

    static sendInvoiceUrl(orderId: string): string {
        return `${this.getByIdUrl(orderId)}/send-invoice`;
    }
}

class OrderBillingInformationApiRoutes {
    static getRootUrl(): string {
        return 'api/order-billing-information';
    }

    static getByIdUrl(orderId: string): string {
        return `${this.getRootUrl()}/${orderId}`;
    }
}

class ReportApiRoutes {
    static getRootUrl(): string {
        return 'api/reports';
    }

    static getNewOrdersReportUrl(): string {
        return `${this.getRootUrl()}/new-orders`;
    }

    static getNewOrdersReportInclusionUrl(): string {
        return `${this.getNewOrdersReportUrl()}/inclusion`;
    }

    static getNewPaymentsReportUrl(): string {
        return `${this.getRootUrl()}/new-payments`;
    }

    static getNewPaymentsReportInclusionUrl(): string {
        return `${this.getNewPaymentsReportUrl()}/inclusion`;
    }

    static getUpdatedExportedOrdersReportUrl(): string {
        return `${this.getRootUrl()}/updated-exported-orders`;
    }

    static getUpdatedExportedOrdersReportInclusionUrl(): string {
        return `${this.getUpdatedExportedOrdersReportUrl()}/inclusion`;
    }

    static getAccountingReviewReportUrl(): string {
        return `${this.getRootUrl()}/accounting-review`;
    }
}

class CreditCardApiRoutes {
    static getRootUrl(userId: number): string {
        return `api/users/${userId}/credit-cards`;
    }

    static getByIdUrl(userId: number, creditCardId: number): string {
        return `${this.getRootUrl(userId)}/${creditCardId}`;
    }

    static getEligibleCardsForOrderUrl(
        userId: number,
        orderId: number,
    ): string {
        return `api/users/${userId}/orders/${orderId}/credit-cards/eligible`;
    }
}

class PaymentApiRoutes {
    static getRootUrl(): string {
        return 'api/orders';
    }

    static getByOrderIdUrl(orderId: string): string {
        return `${this.getRootUrl()}/${orderId}`;
    }

    static getPaymentDetailsUrl(orderId: string): string {
        return `${this.getByOrderIdUrl(orderId)}/payment-details`;
    }

    static getPaymentsUrl(orderId: string): string {
        return `${this.getByOrderIdUrl(orderId)}/payments`;
    }

    static getUserPaymentsUrl(userId: number): string {
        return `api/users/${userId}/payments`;
    }

    static getPaymentTransactionDetailsUrl(
        orderId: string,
        paymentId: string,
    ): string {
        return `${this.getByOrderIdUrl(orderId)}/payments/${paymentId}/details`;
    }

    static getPaymentTransactionStatusUrl(
        orderId: string,
        paymentId: string,
    ): string {
        return `${this.getByOrderIdUrl(orderId)}/payments/${paymentId}/status`;
    }

    static voidPaymentUrl(orderId: string, paymentId: string): string {
        return `${this.getByOrderIdUrl(orderId)}/payments/${paymentId}/void`;
    }

    static creditPaymentUrl(orderId: string, paymentId: string): string {
        return `${this.getByOrderIdUrl(orderId)}/payments/${paymentId}/credit`;
    }

    static confirmPaymentUrl(orderId: string, paymentId: string): string {
        return `${this.getByOrderIdUrl(orderId)}/payments/${paymentId}/confirm`;
    }
}

class ProductApiRoutes {
    static getRootUrl(): string {
        return 'api/products';
    }
}

class AttachmentApiRoutes {
    static getRootUrl(applicationId: string): string {
        return `${ApplicationApiRoutes.getRootUrlWithApplicationId(applicationId)}/attachments`;
    }

    static getRootUrlWithAttachmentId(
        applicationId: string,
        attachmentId: string,
    ): string {
        return `${this.getRootUrl(applicationId)}/${attachmentId}`;
    }
}

class GoApplicationRoutes {
    static readonly rootUrl: string = 'api/go/applications';

    static readonly parameters: string = `parameters`;
    static readonly terminateSearch: string = `terminate-search`;
    static readonly cancelSearch: string = `cancel-search`;
    static readonly stopAndSaveSearch: string = `stop-and-save-search`;

    static readonly frequencyResults: string = `frequency-results`;

    static readonly price: string = `price`;

    static readonly submitToEwa: string = `submit-to-ewa`;

    static readonly reRunExpiredApplication: string = `re-run`;

    static getRootUrlWithApplicationId(applicationId: string) {
        return `${this.rootUrl}/${applicationId}`;
    }

    static getFullRouteWithApplicationId(
        route: RouteKey<typeof GoApplicationRoutes>,
        applicationId: string,
    ) {
        return `${this.rootUrl}/${applicationId}/${GoApplicationRoutes[route]}`;
    }
}

class MasqueradeApiRoutes {
    static readonly rootUrl: string = 'api/masquerades';
}

class UserApiRoutes {
    static readonly rootUrl: string = 'api/users';

    static readonly loggedInUser: string = `${this.rootUrl}/logged-in-user`;

    static readonly createFromContact: string = `${this.rootUrl}/from-contact`;

    static getWelcomeBannerStatusUrl(userId: number): string {
        return `${this.rootUrl}/${userId}/welcome-banner-status`;
    }

    static disableWelcomeBannerUrl(userId: number): string {
        return `${this.rootUrl}/${userId}/disable-welcome-banner`;
    }

    static emailVerificationStatusUrl(userId: number): string {
        return `${this.rootUrl}/${userId}/email-verification-status`;
    }

    static sendEmailVerificationEmailUrl(userId: number): string {
        return `${this.rootUrl}/${userId}/send-email-verification`;
    }
}

class UserProfileApiRoutes {
    static readonly rootUrl: string = 'api/user-profiles';

    static getUserAvailabilityUrl(userId: number): string {
        return `${this.rootUrl}/${userId}/availability`;
    }
}

class ValidationApiRoutes {
    static readonly rootUrl: string = 'api/validations';

    static readonly coordinateIsInUSOrTerritories: string = `${this.rootUrl}/coordinate-is-in-us-or-territories`;
}

class ULSApiRoutes {
    static readonly rootUrl: string = 'api/uls';

    static readonly friendliesForFRNs: string = `${this.rootUrl}/friendlies-for-frns`;

    static readonly asrLocation: string = `${this.rootUrl}/asr-location`;

    static readonly hasLicenseData: string = `${this.rootUrl}/has-license-data`;

    static readonly searchEntities: string = `${this.rootUrl}/search-entities`;
}

class Auth0Routes {
    static readonly rootUrl: string = 'api/auth0';

    static readonly resetPassword: string = `${this.rootUrl}/reset-password`;
}

class PublicAppConfigurationApiRoutes {
    static readonly rootUrl: string = 'api/public-app-configuration';
}

/**
 * Class for generating CiviCRM URLs. The base CiviCRM URL is expected
 * to be provided from the API through the public app configuration.
 */
class CiviCrmRoutes {
    /**
     * Generates the URL to view a contact in CiviCRM.
     * @param civiCrmUrl The base URL of the CiviCRM instance.
     * @param contactId The ID of the contact to view.
     * @returns The full URL to view the contact in CiviCRM.
     */
    static getContactUrl(civiCrmUrl: string, contactId: number): string {
        return `${civiCrmUrl}/contact/view?cid=${contactId}`;
    }
}

class ToolsRoutes {
    static readonly rootUrl: string = 'api/tools';
}

export const ApiRouteConstants = {
    Application: ApplicationApiRoutes,
    UnsupportedApplication: UnsupportedApplicationApiRoutes,
    ApplicationTemplate: ApplicationTemplateApiRoutes,
    FrnFiling: FrnFilingApiRoutes,
    Order: OrderApiRoutes,
    OrderBillingInformation: OrderBillingInformationApiRoutes,
    Report: ReportApiRoutes,
    CreditCard: CreditCardApiRoutes,
    Payment: PaymentApiRoutes,
    Product: ProductApiRoutes,
    Attachment: AttachmentApiRoutes,
    GoApplication: GoApplicationRoutes,
    Masquerade: MasqueradeApiRoutes,
    User: UserApiRoutes,
    UserProfile: UserProfileApiRoutes,
    Validation: ValidationApiRoutes,
    ULS: ULSApiRoutes,
    Auth: Auth0Routes,
    PublicAppConfigurationApiRoutes: PublicAppConfigurationApiRoutes,
    CiviCrm: CiviCrmRoutes,
    ServiceFulfillment: ServiceFulfillmentApiRoutes,
    Tools: ToolsRoutes,
};
